import React from "react"
import { Stack, Box, Link, Typography, useTheme } from "@carrotfertility/carotene-core"
import { HomepageHelp } from "./components/homepage/HomepageHelp"
import { ProviderSearchForm } from "./components/homepage/ProviderSearchForm"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { Helmet } from "react-helmet"
import { useIntl, FormattedMessage } from "react-intl"
import { SearchType, useSearchTypeContext } from "./context/SearchTypeContext"
import { Redirect, Link as RouterLink, useLocation } from "react-router-dom"
import useGetCustomNetworks from "#/components/views/app/navigation/hooks/useGetCustomNetworks"
import { Loading } from "./components/Loading"
import { providerFinderRootPath } from "./utils/providerFinderSearchUrl"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader, PageHeaderGlyph } from "#/features/global-ui/page-header"

const HeadingText = ({
  isCustomNetworks,
  isCarrotCore
}: {
  isCustomNetworks: boolean
  isCarrotCore: boolean
}): JSX.Element => {
  const intl = useIntl()
  const searchText = isCustomNetworks ? (
    <FormattedMessage defaultMessage="Search for providers in your network" />
  ) : (
    <FormattedMessage defaultMessage="Search for providers in the Carrot network" />
  )

  const findProviderText = (() => {
    if (isCustomNetworks) {
      return (
        <FormattedMessage defaultMessage="Find a provider that meets your needs and meets Carrot’s standards of excellence." />
      )
    }
    if (isCarrotCore) {
      return (
        <FormattedMessage defaultMessage="Find a provider that meets your needs. We've also partnered with select providers to help you save on costs and book priority appointments." />
      )
    }
    return (
      <FormattedMessage
        defaultMessage={
          "Find providers where you can use Carrot funds for <link>eligible care and services</link>. " +
          "Our experts have vetted all listed providers to ensure the highest standards."
        }
        values={{
          link: (linkContent) => (
            <Link
              component={RouterLink}
              color="inherit"
              to="/your-benefit-guide/coverage"
              title={intl.formatMessage({ defaultMessage: "Read about your eligible care and services" })}
            >
              {linkContent}
            </Link>
          )
        }}
      />
    )
  })()

  return (
    <Box>
      <Typography variant="h5" component="h2" marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}>
        {searchText}
      </Typography>
      <Typography variant="body1" color={(theme) => theme.palette.text.secondary}>
        {findProviderText}
      </Typography>
    </Box>
  )
}

export function ProviderHomepage({ benefit }: { benefit: Benefit }): JSX.Element {
  const { data, isLoading: isCustomNetworksLoading } = useGetCustomNetworks()
  const isCustomNetworks = data?.hasCustomNetworks

  const intl = useIntl()
  const theme = useTheme()
  const { pathname } = useLocation()
  const { searchType, setSearchType } = useSearchTypeContext()
  const isCarrotCore = benefit.program.isCarrotLite

  if (isCustomNetworksLoading) {
    return <Loading />
  }

  if (!isCustomNetworks && isCarrotCore && pathname === `${providerFinderRootPath}/search-by-name`) {
    setSearchType(SearchType.LOCATION)
    return <Redirect to={providerFinderRootPath} />
  }
  if (pathname === providerFinderRootPath && searchType === SearchType.KEYWORD) {
    setSearchType(SearchType.LOCATION)
  }
  return (
    <>
      <PageLayout
        header={
          <Stack marginBottom={theme.spacing(theme.tokens.spacing.xl)} spacing={theme.spacing(theme.tokens.spacing.lg)}>
            <PageHeader
              startGlyph={<PageHeaderGlyph src="/images/icn-providers.png" srcSet={"/images/icn-providers-2x.png"} />}
              pageTitle={<FormattedMessage defaultMessage="Provider finder" />}
            />
          </Stack>
        }
      >
        <Helmet>
          <title>
            {searchType === SearchType.KEYWORD
              ? intl.formatMessage({
                  defaultMessage: "Provider Finder | Check provider eligibility | Carrot Fertility",
                  description: "The title of the current page as shown in the browser's tab or window."
                })
              : intl.formatMessage({
                  defaultMessage: "Provider Finder | Explore our network | Carrot Fertility",
                  description: "The title of the current page as shown in the browser's tab or window."
                })}
          </title>
        </Helmet>
        <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <HeadingText isCarrotCore={isCarrotCore} isCustomNetworks={isCustomNetworks} />
          <ProviderSearchForm benefit={benefit} isCarrotCore={isCarrotCore} isCustomNetworks={isCustomNetworks} />
          <Box marginTop={(theme) => theme.tokens.spacing.lg}>
            <HomepageHelp />
          </Box>
        </Stack>
      </PageLayout>
    </>
  )
}
